const IMetricsClient = require("./IMetricsClient.js");

const FLUSH_TIMEOUT_MS = 2000;

/**
 * Metrics client backed by the **Node.js** Segment SDK (`analytics-node`).
 *
 * Unlike the browser snippet, analytics-node has no ambient user and takes a
 * single options object, so the acting `userId` MUST be supplied on every call.
 * The call shapes mirror the existing `@easil/au.com.easil.common` SegmentClient
 * so backend call sites move across unchanged.
 */
class SegmentNodeClient extends IMetricsClient {
  /**
   * @param {object} analytics An analytics-node instance.
   * @param {object} [options] See {@link IMetricsClient} (e.g. `onError`).
   */
  constructor(analytics, options = {}) {
    super(options);
    this.analytics = analytics;
  }

  track(userId, eventName, properties = {}) {
    if (!this.analytics) return;
    this.safeInvoke(() =>
      this.analytics.track({ userId, event: eventName, properties })
    );
  }

  identify(userId, traits = {}) {
    if (!this.analytics) return;
    this.safeInvoke(() => this.analytics.identify({ userId, traits }));
  }

  group(groupId, userId, groupTraits = {}) {
    if (!this.analytics) return;
    this.safeInvoke(() =>
      this.analytics.group({ groupId, userId, traits: groupTraits })
    );
  }

  async flush() {
    if (!this.analytics || typeof this.analytics.flush !== "function") return;
    // analytics-node's flush is callback-based — awaiting its return resolves
    // before the queue drains and a shutdown hook can drop events. Wrap the
    // callback in a Promise (also supporting SDKs that return one), and bound it
    // with a timeout so a stalled flush can never hang the shutdown path. Errors
    // route to onError; the Promise never rejects.
    await new Promise(resolve => {
      let settled = false;
      let timer;
      const done = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) this.reportError(error);
        resolve();
      };
      timer = setTimeout(() => done(), FLUSH_TIMEOUT_MS);
      try {
        const maybePromise = this.analytics.flush(error => done(error));
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.then(() => done(), done);
        }
      } catch (error) {
        done(error);
      }
    });
  }
}

module.exports = SegmentNodeClient;
